import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, auth, provider } from "../firebase";
import { signInWithPopup, onAuthStateChanged } from "firebase/auth";
import Toast from "../components/Toast";
import "./RegisterForm.css";

export default function RegisterForm() {
  const { id } = useParams(); // event id
  const nav = useNavigate();

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [user, setUser] = useState(null);
  const [registered, setRegistered] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  /** Toast helper */
  const showToast = (type, message, duration = 2500) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: "", message: "" }), duration);
  };

  /** Track Firebase auth state */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
        setForm((f) => ({
          ...f,
          name: u.displayName || "",
          email: u.email || "",
        }));
      } else {
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  /** Load event */
  useEffect(() => {
    const loadEvent = async () => {
      setLoadingEvent(true);
      try {
        const snap = await getDoc(doc(db, "events", id));
        if (snap.exists()) {
          setEvent({ id: snap.id, ...snap.data() });
        } else {
          showToast("error", "Event not found");
        }
      } catch (err) {
        console.error(err);
        showToast("error", "Failed to load event");
      } finally {
        setLoadingEvent(false);
      }
    };
    loadEvent();
  }, [id]);

  /** Register user in Firestore */
  const registerUser = async (data) => {
    await addDoc(collection(db, "registrations"), {
      ...data,
      eventId: id,
      timestamp: serverTimestamp(),
    });
    setRegistered(true);
    showToast("success", "Registered successfully!");
    setTimeout(() => nav("/success"), 1200);
  };

  /** Google register/login */
  const handleGoogleRegister = async () => {
    setSubmitting(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const u = result.user;

      setUser(u);
      setForm((f) => ({
        ...f,
        name: u.displayName || "",
        email: u.email || "",
      }));

      await registerUser({
        name: u.displayName || "Unknown",
        email: u.email,
        phone: form.phone || "",
        userId: u.uid,
      });
    } catch (err) {
      console.error(err);
      showToast("error", "Google login/register failed");
    } finally {
      setSubmitting(false);
    }
  };

  /** Manual form submit */
  const handleManualRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerUser({
        name: form.name,
        email: form.email,
        phone: form.phone,
        userId: user?.uid || null,
      });
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to register");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEvent) return <div className="loading">Loading event...</div>;
  if (!event) return <div className="loading">Event not found.</div>;

  return (
    <div className="register-container">
      <Toast show={toast.show} type={toast.type} message={toast.message} />

      <div className="register-card">
        <h2>Register for: {event.title}</h2>
        <p className="event-meta">
          {event.date} • {event.location}
        </p>

        {!registered ? (
          <>
            {/* Google Register */}
            <button
              className="btn-google"
              onClick={handleGoogleRegister}
              disabled={submitting}
            >
              {submitting ? "Registering..." : "Register / Login with Google"}
            </button>

            {/* Manual Form */}
            <form className="register-form" onSubmit={handleManualRegister}>
              <label>Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />

              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />

              <label>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />

              <button
                type="submit"
                className="btn-register"
                disabled={submitting}
              >
                {submitting ? "Registering..." : "Register"}
              </button>
            </form>
          </>
        ) : (
          <p className="registered-msg">🎉 You are registered for this event!</p>
        )}
      </div>
    </div>
  );
}
